import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ArrowRight, Search, Heart, MessageCircle, CheckCircle } from 'lucide-react';

const CustomerOnboarding = ({ user, onComplete }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if onboarding was already completed
    const onboardingCompleted = localStorage.getItem(`customer_onboarding_${user?.id}`);
    if (!onboardingCompleted && user?.role === 'customer') {
      setIsVisible(true);
    }
  }, [user]);

  const steps = [
    {
      title: 'Willkommen bei EventConnect! 🎉',
      description: 'Wir helfen dir, die perfekten Dienstleister für dein Event zu finden. Lass uns dir zeigen, wie es funktioniert!',
      icon: null,
      action: null
    },
    {
      title: 'Dienstleister suchen 🔍',
      description: 'Durchsuche über 16 Kategorien mit mehr als 100 spezialisierten Event-Dienstleistern. Filtere nach Stadt, Kategorie und Preisspanne.',
      icon: <Search className="w-12 h-12 text-[#2B2142]" />,
      action: { label: 'Zur Suche', path: '/search' }
    },
    {
      title: 'Favoriten speichern ❤️',
      description: 'Speichere interessante Dienstleister in deinen Favoriten und organisiere sie in Listen für verschiedene Events.',
      icon: <Heart className="w-12 h-12 text-red-500" />,
      action: null
    },
    {
      title: 'Anfragen senden 📨',
      description: 'Kontaktiere Dienstleister kostenlos und unverbindlich. Du erhältst schnell eine Antwort – meist in unter 2 Stunden!',
      icon: <MessageCircle className="w-12 h-12 text-green-500" />,
      action: null
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    localStorage.setItem(`customer_onboarding_${user?.id}`, 'completed');
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  const handleAction = (path) => {
    // Complete onboarding first
    handleComplete();
    // Then navigate after a short delay
    if (path) {
      setTimeout(() => navigate(path), 0);
    }
  };

  if (!isVisible) return null;

  const step = steps[currentStep];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-8 relative animate-in fade-in slide-in-from-bottom-4 duration-300">
        {/* Close Button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex gap-2 mb-2">
            {steps.map((_, idx) => (
              <div
                key={idx}
                className={`flex-1 h-2 rounded-full transition-all ${
                  idx <= currentStep ? 'bg-[#2B2142]' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
          <p className="text-sm text-gray-600 text-right">
            Schritt {currentStep + 1} von {steps.length}
          </p>
        </div>

        {/* Content */}
        <div className="text-center">
          {step.icon && (
            <div className="mb-6 flex justify-center">
              {step.icon}
            </div>
          )}
          
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{step.title}</h2>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed">{step.description}</p>

          {/* Actions */}
          <div className="flex gap-4">
            {currentStep > 0 && (
              <button
                onClick={() => setCurrentStep(currentStep - 1)}
                className="flex-1 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
              >
                Zurück
              </button>
            )}
            
            {step.action ? (
              <button
                onClick={() => handleAction(step.action.path)}
                className="flex-1 py-3 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors"
              >
                {step.action.label}
                <ArrowRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="flex-1 py-3 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors"
              >
                {currentStep === steps.length - 1 ? 'Los geht\'s!' : 'Weiter'}
                <ArrowRight className="w-5 h-5" />
              </button>
            )}
          </div>

          {currentStep === 0 && (
            <button
              onClick={handleSkip}
              className="mt-4 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Tour überspringen
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerOnboarding;
